import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { findAndRemove } from '../../../helpers';
import { RecordingsService } from '../recordings.service';
import { findAndReplaceByIdWithSort, RecordingsStore } from '../recordings.store';

export const getRecordingConfigurationMethods = (injects: { store: RecordingsStore; recordingsService: RecordingsService }) => {
  const { store, recordingsService } = injects;

  const addFieldExpert = rxMethod<{ recordingId: string; fieldExpertId: string }>(
    pipe(
      tap(() => patchState(store, { isFieldExpertsLoading: true })),
      switchMap(({ recordingId, fieldExpertId }) =>
        recordingsService.addFieldExpert(recordingId, fieldExpertId).pipe(
          tap(recording => {
            patchState(store, {
              selectedRecording: recording,
              isFieldExpertsLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isFieldExpertsLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const removeFieldExpert = rxMethod<{ recordingId: string; fieldExpertId: string }>(
    pipe(
      tap(() => patchState(store, { isFieldExpertsLoading: true })),
      switchMap(({ recordingId, fieldExpertId }) =>
        recordingsService.deleteFieldExpert(recordingId, fieldExpertId).pipe(
          tap(() => {
            const selectedRecording = store.selectedRecording()!;
            patchState(store, {
              selectedRecording: {
                ...selectedRecording,
                fieldExperts: findAndRemove(selectedRecording.fieldExperts, entity => entity.id === fieldExpertId),
              },
              isFieldExpertsLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isFieldExpertsLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const setRecordingMembershipLevels = rxMethod<{ id: string; membershipLevels: string[] }>(
    pipe(
      tap(() => patchState(store, { isSelectedRecordingLoading: true, isLoading: true })),
      switchMap(({ id, membershipLevels }) =>
        recordingsService.setMembershipLevels(id, membershipLevels).pipe(
          tap(updatedRecording => {
            patchState(store, {
              recordings: findAndReplaceByIdWithSort(store.recordings(), updatedRecording),
              selectedRecording: updatedRecording,
              isSelectedRecordingLoading: false,
              isLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedRecordingLoading: false, isLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );
  const setRecordingWorkloads = rxMethod<{ id: string; workloads: string[] }>(
    pipe(
      tap(() => patchState(store, { isSelectedRecordingLoading: true, isLoading: true })),
      switchMap(({ id, workloads }) =>
        recordingsService.setWorkloads(id, workloads).pipe(
          tap(updatedRecording => {
            patchState(store, {
              recordings: findAndReplaceByIdWithSort(store.recordings(), updatedRecording),
              selectedRecording: updatedRecording,
              isSelectedRecordingLoading: false,
              isLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedRecordingLoading: false, isLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  return {
    addFieldExpert,
    removeFieldExpert,
    setRecordingMembershipLevels,
    setRecordingWorkloads,
  };
};

export interface RecordingConfigurationStoreType {
  readonly addFieldExpert: (params: { recordingId: string; fieldExpertId: string }) => void;
  readonly removeFieldExpert: (params: { recordingId: string; fieldExpertId: string }) => void;
  readonly setRecordingMembershipLevels: (params: { id: string; membershipLevels: string[] }) => void;
  readonly setRecordingWorkloads: (params: { id: string; workloads: string[] }) => void;
}
